/**
 * Axios client for the Hello by Flow backend.
 *
 * Cookie-only auth (post-iter-36 hard cut):
 *   - `withCredentials: true` sends HttpOnly `access_token` / `refresh_token`.
 *   - JS-readable `csrf_token` cookie is mirrored into the `X-CSRF-Token`
 *     header on every unsafe (POST/PATCH/DELETE/PUT) request.
 *   - On 401 we transparently call POST /auth/refresh ONCE and retry the
 *     original request. If refresh also fails we surface the 401 to the
 *     caller; AuthContext will flip user→false and redirect to /login.
 *
 * The previous Bearer interceptor that read `hbf_token` (formerly `vocalis_token`) from
 * localStorage was removed in iter 36. localStorage is reserved for UI
 * preferences (theme, sidebar, last route) only.
 */

import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

const CSRF_COOKIE = "csrf_token";
const CSRF_HEADER = "X-CSRF-Token";
const UNSAFE = ["post", "put", "patch", "delete"];

function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

async function ensureCsrf() {
  if (readCookie(CSRF_COOKIE)) return readCookie(CSRF_COOKIE);
  try {
    // Use the shared axios instance so `withCredentials` (needed to accept
    // the Set-Cookie response) and baseURL are applied consistently.
    await axios.get(`${API}/auth/csrf`, { withCredentials: true });
  } catch (_) { /* ignored — request below will surface 403 */ }
  // Some browsers make the just-set cookie readable only after the current
  // microtask. Give the event loop one tick before reading document.cookie.
  await new Promise((r) => setTimeout(r, 0));
  return readCookie(CSRF_COOKIE);
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,  // HttpOnly cookies travel automatically
});

// ---- Request: inject CSRF on unsafe methods ----
api.interceptors.request.use(async (cfg) => {
  if (UNSAFE.includes((cfg.method || "get").toLowerCase())) {
    const token = await ensureCsrf();
    if (token) cfg.headers[CSRF_HEADER] = token;
  }
  return cfg;
});

// ---- Response: silent refresh on 401 + retry ----
let refreshPromise = null;
async function silentRefresh() {
  if (refreshPromise) return refreshPromise;  // coalesce concurrent 401s
  refreshPromise = axios
    .post(`${API}/auth/refresh`, null, {
      withCredentials: true,
      headers: { [CSRF_HEADER]: readCookie(CSRF_COOKIE) || "" },
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (!original) return Promise.reject(error);
    const url = original.url || "";
    const isAuthEndpoint = /\/auth\/(login|refresh|logout|csrf)/.test(url);

    // ── Session superseded — Agent/Supervisor single-active-session enforcement.
    // The backend returns 401 with `detail={ code: "session_superseded", … }`
    // when this account was signed in on another device. Hard-redirect to
    // /login?superseded=1 so the user sees the banner instead of the app
    // limping along with 401s in the background.
    const raw = error.response?.data?.detail;
    const supersededCode =
      (raw && typeof raw === "object" && raw.code === "session_superseded") ||
      (typeof raw === "string" && raw.toLowerCase().includes("logged in on another device"));
    if (error.response?.status === 401 && supersededCode) {
      try {
        if (typeof window !== "undefined" && !/\/login\b/.test(window.location.pathname)) {
          window.location.replace("/login?superseded=1");
        }
      } catch (_) { /* noop */ }
      return Promise.reject(error);
    }

    // ── CSRF race — some flows fire an unsafe request before the CSRF cookie
    // is settled in the browser's cookie jar. When the backend reports the
    // cookie missing (400 with that exact phrase), refetch CSRF once and
    // retry. This closes a real race we saw in BusinessFunnel step 2.
    const detail = error.response?.data?.detail;
    const looksLikeCsrfMiss =
      error.response?.status === 400 &&
      typeof detail === "string" &&
      detail.toLowerCase().includes("csrf");
    if (looksLikeCsrfMiss && !original._csrfRetried) {
      try {
        await axios.get(`${API}/auth/csrf`, { withCredentials: true });
        await new Promise((r) => setTimeout(r, 20));
        original._csrfRetried = true;
        const t = readCookie(CSRF_COOKIE);
        if (t) original.headers[CSRF_HEADER] = t;
        return api(original);
      } catch (_) { /* fall through to reject */ }
    }

    if (error.response?.status !== 401 || original._retried || isAuthEndpoint) {
      return Promise.reject(error);
    }
    try {
      await silentRefresh();
      original._retried = true;
      return api(original);
    } catch (e) {
      return Promise.reject(error);
    }
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
